<template>
  <div :data-test-id="testId" class="tw-relative">
    <SelectRoot
      v-model="selectedOption"
      :disabled="isDisabled"
      v-model:open="isOpen"
    >
      <SelectTrigger
        class="tw-justify-between tw-text-base tw-items-center tw-w-full tw-py-2 tw-px-3 tw-flex tw-rounded tw-border tw-border-primary-grey-300/80 tw-bg-white tw-text-primary-grey-950 tw-outline-none tw-ring-offset-1 tw-transition-all"
        :class="[classes]"
        :aria-label="placeholder"
      >
        <span>{{ selectedText || placeholder }}</span>
        <ClIconTablerChevronDown
          class="tw-transition-transform tw-text-primary-grey-300"
          :class="chevronIconClass"
          :width="'18px'"
          :height="'18px'"
        />
      </SelectTrigger>

      <SelectContent
        class="tw-min-w-[160px] tw-bg-white tw-rounded-md tw-border tw-border-primary-grey-300/80 tw-shadow-md tw-z-[100] tw-overflow-hidden tw-animate-fade-in tw-max-h-[--reka-select-content-available-height] tw-w-[--reka-select-trigger-width]"
        :side-offset="0"
        position="popper"
      >
        <SelectViewport>
          <slot />
        </SelectViewport>
      </SelectContent>
    </SelectRoot>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, provide, watch } from "vue";
import {
  SelectContent,
  SelectRoot,
  SelectTrigger,
  SelectViewport,
} from "reka-ui";
import { ClIconTablerChevronDown } from "../icons";

interface IProps {
  placeholder: string;
  isDisabled?: boolean;
  testId?: string;
  modelValue: string;
}

withDefaults(defineProps<IProps>(), {
  isDisabled: false,
  testId: "",
});

const emit = defineEmits<{
  (event: "opened"): void;
}>();

const isOpen = ref(false);
const valueToText = ref<Record<string, string>>({});
const selectedOption = defineModel<string>();
const chevronIconClass = computed(() =>
  isOpen.value ? "tw-rotate-180" : "tw-rotate-0",
);
const focusClasses =
  "focus:tw-border-primary-grey-500 focus:tw-ring-4 focus:tw-ring-primary-grey-500/10";

const disabledClasses =
  "disabled:tw-cursor-not-allowed disabled:tw-bg-primary-grey-50 disabled:tw-text-primary-grey-700/50 disabled:tw-outline-none";

const classes = computed(() => {
  return [focusClasses, disabledClasses];
});

const selectedText = computed(() => {
  const key = selectedOption.value;
  return key ? valueToText.value[key] || "" : "";
});

const registerItemText = (value: string, text: string) => {
  valueToText.value[value] = text;
};

// We use provide&inject to allow each ClSelectItem component to register its selected value text.
// Since we don't want to couple SelectItem to a specific implementation of ClSelect
// or require the user to pass extra props, we use provide/inject.
// The parent uses this information to map the selected value to its display selected value text.
// Alternative method was to use 'text-value' from rekaui docs, but it doesnt work
// (https://reka-ui.com/docs/components/select#item)
provide("registerItemText", registerItemText);

watch(
  () => isOpen.value,
  () => {
    if (isOpen.value) {
      emit("opened");
    }
  },
);
</script>
